import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Chip, CircularProgress, Collapse, Divider, Grid, IconButton, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useParams } from 'react-router-dom'
import NoDataAvailableCard from './NoDataAvailableCard';
import GraphCard from './GraphCard';
import StatTable from './StatTable';
import CardSkeleton from './CardSkeleton';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { getUtilizationAssetsApi } from '../apiService';
import CardFilter from './CardFilter';

const UtilizationMonthlyTrends = ({ utilizationData, isLoading = {}, assetData, onAssetChange, utilType, onMetricChange, utilVal, onPeriodChange, trendMetric, onTrendMetricChange, filters = {}, onFilterChange, cardFiltersDisabled = false }) => {
  const params = useParams();
  const isMonthlyUtilLoading = isLoading?.monthly_utilization
  const [isDrillDown, setDrillDown] = useState(false);
  const [majorAssemblyOptions, setMajorAssemblyOptions] = useState([])
  const [majorAssemblyLoading, setMajorAssemblyLoading] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [expandedComponents, setExpandedComponents] = useState({})

  const monthlyUtil = utilizationData?.monthly_utilization;

  useEffect(() => {
    getUtilizationAssets();
  }, [])

  useEffect(() => {
    setSelectedMonth(null);
    if (!monthlyUtil?.options) setDrillDown(false);
  }, [monthlyUtil])

  useEffect(() => {
    setExpandedComponents({});
  }, [utilizationData?.average_utilization])

  const toggleComponentDetails = (key) => {
    setExpandedComponents(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const handleMonthClick = (chartParams) => {
    if (chartParams?.componentType !== 'series') return;
    const trendItem = monthlyUtil?.utilsTrendsData?.[chartParams?.dataIndex];
    if (!trendItem?.month) return;
    setSelectedMonth(trendItem.month);
    setDrillDown(true);
  }

  const drillDownRows = useMemo(() => (
    selectedMonth
      ? (monthlyUtil?.table?.rows || []).filter(row => row.period === selectedMonth)
      : monthlyUtil?.table?.rows
  ), [selectedMonth, monthlyUtil])

  const getUtilizationAssets = () => {
    setMajorAssemblyLoading(true)
    globalGetService(getUtilizationAssetsApi(params)).then(response => {
      setMajorAssemblyLoading(false)
      if (checkApiStatus(response)) {
        setMajorAssemblyOptions(response?.data?.data || [])
      }
    })
  }
  return (
    <div className="contract-analytics-wrapper">
      <div className="ca-util-trends">
        <div className="ca-util-title-row">
          <Typography className="ca-util-title">Utilization & Component Trends</Typography>
        </div>
        <Grid container spacing={1}>
          <Grid size={{ md: 6, xs: 12 }} sx={{ display: 'flex' }}>
            <Paper elevation={0} className="ca-util-card">
              <CardFilter
                disabled={cardFiltersDisabled}
                headerClassName="ca-util-card-header"
                titleClassName="ca-util-card-title"
                title="Monthly Utilization Trend — Actual FH & FC"
                filter={filters.monthly_utilization}
                onChange={(filter) => onFilterChange('monthly_utilization', filter)}
                actions={
                  <div className="ca-util-header-actions">
                    <Autocomplete
                      disablePortal
                      disableClearable
                      className="ca-util-msn-select"
                      options={majorAssemblyOptions}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      disabled={isMonthlyUtilLoading || majorAssemblyLoading}
                      getOptionLabel={(option) => option?.name || ""}
                      noOptionsText={majorAssemblyLoading ? "loading" : "No Option Available"}
                      value={majorAssemblyOptions.find(item => item?.id === assetData?.asset_id) || null}
                      onChange={(e, value) => onAssetChange(value ? { asset_id: value?.id, asset_type: value?.asset_type, asset_slug: value?.asset_slug, name: value?.name } : null)}
                      renderInput={(params) => {
                        const selectedLabel = params?.inputProps?.value || "";
                        return (
                          <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow>
                            <TextField
                              {...params}
                              margin='none'
                              size='small'
                              fullWidth
                              disabled={isMonthlyUtilLoading || majorAssemblyLoading}
                              slotProps={{
                                inputLabel: { shrink: true },
                                input: {
                                  ...params?.InputProps, endAdornment: (<>{majorAssemblyLoading ? (<CircularProgress color="inherit" size={20} />) : null}{params?.InputProps?.endAdornment}</>)
                                }
                              }}
                            />
                          </Tooltip>
                        );
                      }}
                    />
                    {monthlyUtil?.table?.rows?.length > 0 &&
                      <Tooltip title={isDrillDown ? "Hide drill down" : "Drill down"}>
                        <IconButton
                          size='small'
                          color={isDrillDown ? 'primary' : 'default'}
                          onClick={() => {
                            if (isDrillDown) setSelectedMonth(null);
                            setDrillDown(!isDrillDown);
                          }}
                        >
                          <UnfoldMoreOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>}
                  </div>
                }
              />
              {isMonthlyUtilLoading ? (
                <CardSkeleton height='260px' className='ca-graph-container' />
              ) : (
                <>
                  <div className="ca-graph-container">
                    {!monthlyUtil?.options ? (
                      <NoDataAvailableCard title='No utilization data available' subtext='Try adjusting the date range' />
                    ) : (
                      <GraphCard option={monthlyUtil?.options} height='260px' onChartClick={handleMonthClick} widenClickArea />
                    )}
                  </div>
                  {isDrillDown &&
                    <div className="ca-drilldown">
                      {selectedMonth &&
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
                          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Showing:</Typography>
                          <Chip label={selectedMonth} size="small" onDelete={() => setSelectedMonth(null)} />
                        </Box>}
                      {drillDownRows?.length ? (
                        <div className="ca-drilldown-scroll">
                          <StatTable columns={monthlyUtil?.table?.columns} rows={drillDownRows} />
                        </div>
                      ) : selectedMonth ? (
                        <NoDataAvailableCard title={`No component data available for ${selectedMonth}`} subtext='Try selecting another month' />
                      ) : (
                        <NoDataAvailableCard title='No component data available' subtext='Try adjusting the date range' />
                      )}
                    </div>}
                </>
              )}
            </Paper>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} sx={{ display: 'flex' }}>
            <Paper elevation={0} className="ca-util-card">
              <CardFilter
                disabled={cardFiltersDisabled}
                headerClassName="ca-util-card-header"
                titleClassName="ca-util-card-title"
                title="Component Utilization Trend Lines"
                filter={filters.component_utilization_trends}
                onChange={(filter) => onFilterChange('component_utilization_trends', filter)}
                actions={
                  <Select
                    size="small"
                    className="ca-util-dropdown"
                    value={trendMetric}
                    disabled={isLoading?.component_utilization_trends}
                    onChange={(e) => onTrendMetricChange(e.target.value)}
                    sx={{ width: 75 }}
                  >
                    <MenuItem value='fh'>FH</MenuItem>
                    <MenuItem value='fc'>FC</MenuItem>
                  </Select>
                }
              />
              {isLoading?.component_utilization_trends ? (
                <CardSkeleton height='260px' className='ca-graph-container' />
              ) : (
                <div className="ca-graph-container">
                  {!utilizationData?.component_utilization_trends?.options ? (
                    <NoDataAvailableCard title='No utilization data available' subtext='Try adjusting the date range' />
                  ) : (
                    <GraphCard option={utilizationData?.component_utilization_trends?.options} height='260px' />
                  )}
                </div>
              )}
            </Paper>
          </Grid>
          <Grid size={12}>
            <Paper elevation={0} className="ca-util-card">
              <CardFilter
                disabled={cardFiltersDisabled}
                headerClassName="ca-util-card-header"
                titleClassName="ca-util-card-title"
                title="Average Utilization by Contract Component"
                filter={filters.average_utilization}
                onChange={(filter) => onFilterChange('average_utilization', filter)}
                actions={
                  <div className="ca-util-header-actions">
                    <Select
                      size="small"
                      className="ca-util-dropdown"
                      value={utilType}
                      disabled={isLoading?.average_utilization}
                      onChange={(e) => onMetricChange(e.target.value)}
                      sx={{ width: 100 }}
                    >
                      <MenuItem value='both'>FH & FC</MenuItem>
                      <MenuItem value='fh'>FH</MenuItem>
                      <MenuItem value='fc'>FC</MenuItem>
                    </Select>
                    <Select
                      size="small"
                      className="ca-util-dropdown"
                      value={utilVal}
                      disabled={isLoading?.average_utilization}
                      onChange={(e) => onPeriodChange(e.target.value)}
                      sx={{ width: 100 }}
                    >
                      <MenuItem value="quarterly">Quarterly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </div>
                }
              />
              {isLoading?.average_utilization ? (
                <CardSkeleton height='150px' className='ca-graph-container' />
              ) : (
                <div className="ca-util-card-body">
                  {!utilizationData?.average_utilization?.items?.length ? (
                    <NoDataAvailableCard title='No average utilization data available' subtext='Try adjusting the date range' />
                  ) : (
                    <div className="ca-util-metrics-row">
                      {utilizationData.average_utilization.items.map((item) => {
                        const componentKey = item.asset_id ?? item.component;
                        const hasDetails = Array.isArray(item.details_report) && item.details_report.length > 0;
                        const isExpanded = !!expandedComponents[componentKey];
                        return (
                          <div className="ca-util-metric-card" key={componentKey}>
                            <div
                              className={`ca-util-metric-box${hasDetails ? ' has-details' : ''}${isExpanded ? ' is-expanded' : ''}`}
                              onClick={hasDetails ? () => toggleComponentDetails(componentKey) : undefined}
                            >
                              <div className="ca-util-metric-name-row">
                                <Typography className="ca-util-metric-name">{item.component}</Typography>
                                {hasDetails &&
                                  <Tooltip title={isExpanded ? "Hide period breakdown" : "Show period breakdown"}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); toggleComponentDetails(componentKey); }}
                                      sx={{ mr: -0.75, transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}
                                    >
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>}
                              </div>
                              <div className="ca-util-metric-badge">{item.period_label || "--"}</div>
                              <div className="ca-util-metric-values">
                                {utilType !== 'fc' &&
                                  <div className="ca-util-metric-value-block">
                                    <Typography className="label">Avg. FH</Typography>
                                    <Typography className="value fh">
                                      {item.avg_monthly_fh || "--"}
                                      <Typography component="span" className="unit"> FH</Typography>
                                    </Typography>
                                  </div>}
                                {utilType === 'both' && <Divider orientation="vertical" flexItem />}
                                {utilType !== 'fh' &&
                                  <div className="ca-util-metric-value-block">
                                    <Typography className="label">Avg. FC</Typography>
                                    <Typography className="value fc">
                                      {item.avg_monthly_fc || "--"}
                                      <Typography component="span" className="unit"> FC</Typography>
                                    </Typography>
                                  </div>}
                              </div>
                            </div>
                            {hasDetails &&
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <div className="ca-util-metric-detail">
                                  {item.details_report.map((d, idx) => (
                                    <div className="ca-util-metric-detail-row" key={`${componentKey}-${d.period}-${idx}`}>
                                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>{d.period}</Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {utilType !== 'fc' &&
                                          <Typography sx={{ fontSize: 11, color: '#2563EB', minWidth: 52, textAlign: 'right' }}>
                                            {d.fh ?? "--"} <Typography component="span" sx={{ fontSize: 9, color: '#94A3B8' }}>FH</Typography>
                                          </Typography>}
                                        {utilType !== 'fh' &&
                                          <Typography sx={{ fontSize: 11, color: '#16A34A', minWidth: 52, textAlign: 'right' }}>
                                            {d.fc ?? "--"} <Typography component="span" sx={{ fontSize: 9, color: '#94A3B8' }}>FC</Typography>
                                          </Typography>}
                                      </Box>
                                    </div>
                                  ))}
                                </div>
                              </Collapse>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default UtilizationMonthlyTrends
